import { OutlinedInput, useForkRef } from "@mui/material";
import { useFormat } from "@sinch/core/hooks/format";
import { isEmpty } from "ramda";
import { isNilOrEmpty, isNotNilOrEmpty } from "ramda-adjunct";
import React, { ForwardedRef, forwardRef, ReactElement, useEffect, useRef, useState } from "react";
import { FormControlWrapper, TextInputProps } from "@sinch/components";
import { useNumberInputValue } from "@sinch/components/form/hooks/useNumberInputValue";

export type CurrencyInputProps = Omit<TextInputProps<"number">, "value" | "onChange"> & {
  value: number;
  onChange: (val: number) => void;
};

export const CurrencyInputBase = (
  {
    label,
    FormHelperTextProps: formHelperTextProps,
    FormControlProps: formControlProps,
    helperText,
    errorMessage,
    error,
    required,
    infoTooltip,
    onChange,
    value,
    allowEmpty,
    ...props
  }: CurrencyInputProps,
  // Taken as the second parameter rather than dropped: `forwardRef` warns about a render function that
  // does not accept one, and react-hook-form hands a `ref` in through `{...field}` to focus the field
  // that failed validation.
  forwardedRef: ForwardedRef<HTMLInputElement>
): ReactElement => {
  const [editMode, setEditMode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const forkedRef = useForkRef(forwardedRef, inputRef);
  const { num } = useFormat();

  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.select();
    }
  }, [editMode]);

  const {
    toClosestLimit,
    toNumber,
    value: numberValue,
  } = useNumberInputValue({
    value,
    allowEmpty,
    onChange: (e) => onChange(parseFloat(e.target.value)),
    min: props.inputProps?.min,
    max: props.inputProps?.max,
  });

  const [invalidNumberValue, setInvalidNumberValue] = useState<string | null>(null);

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <OutlinedInput
        error={error}
        {...props}
        fullWidth
        inputProps={{
          ref: forkedRef,
          ...props.inputProps,
          sx: { ...props.inputProps?.sx, textAlign: "right", "&.MuiInputBase-inputSizeSmall": { px: 1 } },
        }}
        onBlur={(e) => {
          props?.onBlur?.(e);
          const newVal = toClosestLimit(e.target.value);
          const sanitizedValue = isEmpty(newVal) ? 0 : parseFloat(newVal);
          if (sanitizedValue !== value) {
            onChange?.(sanitizedValue);
          }
          setEditMode(false);
          setInvalidNumberValue(null);
        }}
        onChange={(e) => {
          if (editMode && isNotNilOrEmpty(e.target.value)) {
            const newVal = toNumber(e.target.value);
            onChange?.(isEmpty(newVal) ? 0 : parseFloat(newVal));
            setInvalidNumberValue(null);
          } else if (isNilOrEmpty(e.target.value)) {
            setInvalidNumberValue(e.target.value);
          }
        }}
        onFocus={(e) => {
          setEditMode(true);
          props.onFocus?.(e);
        }}
        sx={{ height: "100%", ...props.sx }}
        type={editMode ? "number" : "text"}
        value={editMode ? (invalidNumberValue ?? value) : num.currency(numberValue as number)}
      />
    </FormControlWrapper>
  );
};

export const CurrencyInput = forwardRef(CurrencyInputBase) as (
  props: CurrencyInputProps & { ref?: ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof CurrencyInputBase>;
